/**
 * ComputerVideoBlock - A class for handling video functionality
 */
class ComputerVideoBlock {
	static setup() {
		const elements = document.querySelectorAll('.block-computer-frame-container');
		for (const element of elements) {
			const instance = new ComputerVideoBlock(element);
			instance.init();
		}
	}

	constructor(element) {
		this.element = element;
		this.video = this.element.querySelector('video');
		this.volumeControl = this.element.querySelector('.volume-control');
		this.playControl = this.element.querySelector('.play-control');
		this.fullscreenControl = this.element.querySelector('.fullscreen-control');
	}

	init() {
		this.initState();
		this.addEventListeners();
	}

	addEventListeners() {
		if (this.volumeControl) {
			this.volumeControl.addEventListener('click', this.toggleMute.bind(this));
		}
		if (this.playControl) {
			this.playControl.addEventListener('click', this.togglePlay.bind(this));
		}
		if (this.fullscreenControl) {
			this.fullscreenControl.addEventListener('click', this.toggleFullscreen.bind(this));
		}

		// keep UI in sync if fullscreen is exited with ESC
		document.addEventListener('fullscreenchange', () => {
			if (!document.fullscreenElement && this.fullscreenControl) {
				this.fullscreenControl.classList.remove('active');
			}
		});
	}

	/**
	 * Check initial video state and apply correct classes
	 */
	initState() {
		// Play state based on attributes first
		if (this.playControl) {
			if (this.video.hasAttribute('autoplay')) {
				this.playControl.classList.add('playing');
				this.playControl.classList.remove('paused');
			} else {
				this.playControl.classList.add('paused');
				this.playControl.classList.remove('playing');
			}
		}

		// Mute state based on attribute
		if (this.volumeControl) {
			if (this.video.hasAttribute('muted')) {
				this.volumeControl.classList.add('muted');
				this.volumeControl.classList.remove('active');
			} else {
				this.volumeControl.classList.add('active');
				this.volumeControl.classList.remove('muted');
			}
		}

		// Listen for real playback events to adjust UI if autoplay failed
		this.video.addEventListener('play', () => {
			if (this.playControl) {
				this.playControl.classList.add('playing');
				this.playControl.classList.remove('paused');
			}
		});

		this.video.addEventListener('pause', () => {
			if (this.playControl) {
				this.playControl.classList.add('paused');
				this.playControl.classList.remove('playing');
			}
		});
	}

	togglePlay() {
		if (this.video.paused) {
			this.video.play();
			this.playControl.classList.remove('paused');
			this.playControl.classList.add('playing');
		} else {
			this.video.pause();
			this.playControl.classList.remove('playing');
			this.playControl.classList.add('paused');
		}
	}

	toggleMute() {
		this.video.muted = !this.video.muted;
		if (this.video.muted) {
			this.video.setAttribute('muted', '');
			this.volumeControl.classList.remove('active');
			this.volumeControl.classList.add('muted');
		} else {
			this.video.removeAttribute('muted');
			this.volumeControl.classList.remove('muted');
			this.volumeControl.classList.add('active');
		}
	}

	toggleFullscreen() {
		if (!document.fullscreenElement) {
			if (this.video.requestFullscreen) {
				this.video.requestFullscreen();
			} else if (this.video.webkitRequestFullscreen) {
				this.video.webkitRequestFullscreen();
			} else if (this.video.msRequestFullscreen) {
				this.video.msRequestFullscreen();
			}
			this.fullscreenControl.classList.add('active');
		} else {
			if (document.exitFullscreen) {
				document.exitFullscreen();
			} else if (document.webkitExitFullscreen) {
				document.webkitExitFullscreen();
			} else if (document.msExitFullscreen) {
				document.msExitFullscreen();
			}
			this.fullscreenControl.classList.remove('active');
		}
	}
}

if (document.readyState === 'interactive' || document.readyState === 'complete') {
	ComputerVideoBlock.setup();
} else {
	document.addEventListener('DOMContentLoaded', ComputerVideoBlock.setup);
}
